/**
 * `useCycleConfig` family — TanStack Query hooks for the per-cycle
 * `TeamConfig` snapshot (issue #533 Part B, chunk B4).
 *
 * Backed by the B2 + B3 backend endpoints:
 *
 *   * `GET    /api/cycles/:cid/config`                 — read the cycle's config blob
 *   * `PATCH  /api/cycles/:cid/config`                 — deep-merge the cycle's blob (lead-only)
 *   * `POST   /api/cycles/:cid/config/stations`        — append a station (lead-only)
 *   * `PATCH  /api/cycles/:cid/config/stations/:name`  — edit / rename one station (lead-only)
 *   * `DELETE /api/cycles/:cid/config/stations/:name`  — drop a station (lead-only)
 *   * `POST   /api/cycles/:cid/promote-to-team-default`— copy this cycle's
 *     whole config onto the team default template (lead-only)
 *
 * ── Cycle-owned config model ──────────────────────────────────────
 * Since B0/B1 every cycle owns a full `TeamConfig` snapshot. The solver
 * runs from `cycle.config`; `team.config` is only the template copied
 * into *new* cycles. So a cycle can add / remove / rename its own
 * stations without touching the team default — and a lead who has
 * tuned one cycle can promote it back to the team default for future
 * cycles.
 *
 * The station mutations return the **whole** updated `TeamConfig`, so
 * the hooks below seed the cycle-config cache from the response.
 *
 * Cache-key shape mirrors `useCycleOnCallConfig` / `useCycleRelaxOverride`:
 *   ["cycle-config", cycleId, accountId]
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { encodePathName } from "../lib/encodePathName";
import { type ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";

// ─── Types (re-exported from the generated schema) ───────────────────

export type TeamConfig = components["schemas"]["TeamConfig"];

/**
 * R3.8b (#820 / #916) — `enabled` is hand-typed here (not yet in
 * `schema.gen.ts`, regenerated separately). The merged backend
 * (`persistence/models/_config.StationEntry`) carries `enabled: bool =
 * True`: a disabled station stays configured for the cycle but is
 * filtered out of the solve (`_solve_request_builder._stations_from_config`)
 * — a non-destructive, reversible per-cycle off-switch distinct from the
 * destructive Remove. Default `True` keeps every existing cycle
 * byte-identical. Once `schema.gen.ts` is regenerated this `&` collapses
 * to a no-op.
 */
// #1900 — the OpenAPI export names the station schema `-Input` / `-Output`
// since `on_call_config.call_points` nests a third model level (pydantic
// 2.10 splits such trees); both are the same shape.
export type StationEntry = components["schemas"]["StationEntry-Output"] & {
  enabled?: boolean;
};

// ─── Cache keys ──────────────────────────────────────────────────────

const STALE_TIME_MS = 60_000;

export function cycleConfigKey(cycleId: string, accountId: string | null) {
  return ["cycle-config", cycleId, accountId ?? ""] as const;
}

// ─── Read ────────────────────────────────────────────────────────────

/**
 * Read the cycle's own `TeamConfig` snapshot — the config the solver
 * runs this cycle against. Open to any active member of the team.
 */
export function useCycleConfig(cycleId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery<TeamConfig, ApiError | Error>({
    queryKey: cycleConfigKey(cycleId ?? "", accountId),
    enabled: Boolean(cycleId && accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<TeamConfig>(`/api/cycles/${cycleId}/config`),
  });
}

/**
 * Read the cycle's config with an **explicit** account id rather than
 * the ambient `useAuth()` context. Retained as a distinct variant so the
 * roster heatmap can feed it the same account id `useCycleRoster` keys
 * on — sharing `cycleConfigKey(cycleId, accountId)` with any
 * `useCycleConfig()` caller on the same account, byte-identically.
 * Issue #595 (#853: callers now pass `useAuth().accountId` directly
 * rather than a threaded `devAccountId` prop).
 */
export function useCycleConfigByAccount(
  cycleId: string | undefined,
  accountId: string | null,
  opts: { enabled?: boolean } = {},
) {
  const enabledByCaller = opts.enabled ?? true;
  return useQuery<TeamConfig, ApiError | Error>({
    queryKey: cycleConfigKey(cycleId ?? "", accountId),
    enabled: Boolean(cycleId && accountId) && enabledByCaller,
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<TeamConfig>(`/api/cycles/${cycleId}/config`),
  });
}

// ─── Station mutations ───────────────────────────────────────────────
//
// All three return the whole updated `TeamConfig`; the shared
// `onSuccess` seeds the cycle-config cache so the editor re-renders
// from the server's truth without a second round-trip.

function seedCycleConfig(
  qc: ReturnType<typeof useQueryClient>,
  cycleId: string,
  accountId: string | null,
  next: TeamConfig,
) {
  qc.setQueryData(cycleConfigKey(cycleId, accountId), next);
  qc.invalidateQueries({ queryKey: cycleConfigKey(cycleId, accountId) });
}

/** Append a station to this cycle's own config. */
export function useAddCycleStation(cycleId: string | undefined) {
  const { accountId } = useAuth();
  const qc = useQueryClient();
  return useMutation<TeamConfig, ApiError | Error, StationEntry>({
    mutationFn: (body) =>
      apiFetch<TeamConfig>(`/api/cycles/${cycleId}/config/stations`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (next) => seedCycleConfig(qc, cycleId ?? "", accountId, next),
  });
}

/**
 * Edit (and optionally rename, via `body.name`) one station on this
 * cycle's config. `originalName` is the station's current name — the
 * path key the PATCH targets.
 */
export function usePatchCycleStation(cycleId: string | undefined, originalName: string) {
  const { accountId } = useAuth();
  const qc = useQueryClient();
  return useMutation<TeamConfig, ApiError | Error, StationEntry>({
    mutationFn: (body) =>
      apiFetch<TeamConfig>(
        `/api/cycles/${cycleId}/config/stations/${encodePathName(originalName)}`,
        {
          method: "PATCH",
          body: JSON.stringify(body),
        },
      ),
    onSuccess: (next) => seedCycleConfig(qc, cycleId ?? "", accountId, next),
  });
}

/** Drop a station from this cycle's own config. */
export function useDeleteCycleStation(cycleId: string | undefined, stationName: string) {
  const { accountId } = useAuth();
  const qc = useQueryClient();
  return useMutation<TeamConfig, ApiError | Error, void>({
    mutationFn: () =>
      apiFetch<TeamConfig>(
        `/api/cycles/${cycleId}/config/stations/${encodePathName(stationName)}`,
        {
          method: "DELETE",
        },
      ),
    onSuccess: (next) => seedCycleConfig(qc, cycleId ?? "", accountId, next),
  });
}

// ─── Sync team default → cycle (#1434 / #1531) ───────────────────────

/** The synced config plus a factual diff summary for the confirm toast. */
export interface CycleConfigSyncResult extends TeamConfig {
  stations_added: string[];
  stations_removed: string[];
  tiers_changed: boolean;
  /** #1449 — recurring pins (#1426) materialised into this cycle by the
   * sync. Optional so an older backend (which returns no such field) is
   * read as "none added" rather than NaN. */
  recurring_allocations_added?: number;
}

/** #1531 — selective-sync variables: `fields` names the top-level config
 * sections to pull (e.g. `["recurring_allocations"]`); pass `{}` for the
 * original full re-copy. */
export interface CycleConfigSyncVariables {
  fields?: string[];
}

/**
 * Re-copy the TEAM's config template into this cycle (#1434). The
 * cycle's snapshot — including any per-cycle station edits — is
 * replaced wholesale (the caller confirms first against the #1531
 * preview). Members are not part of the snapshot; they load live.
 * Lead-only on the backend; 409 while a solve job is queued or running.
 */
export function useSyncCycleConfigFromTeam(cycleId: string | undefined) {
  const { accountId } = useAuth();
  const qc = useQueryClient();
  return useMutation<CycleConfigSyncResult, ApiError | Error, CycleConfigSyncVariables>({
    mutationFn: (vars) =>
      apiFetch<CycleConfigSyncResult>(`/api/cycles/${cycleId}/config/sync-from-team`, {
        method: "POST",
        ...(vars.fields ? { body: JSON.stringify({ fields: vars.fields }) } : {}),
      }),
    onSuccess: (next) => {
      seedCycleConfig(qc, cycleId ?? "", accountId, next);
      // The drift preview is now stale by construction — re-read it so
      // the indicator clears (or narrows, after a selective sync).
      qc.invalidateQueries({ queryKey: cycleConfigSyncPreviewKey(cycleId ?? "", accountId) });
    },
  });
}

// ─── Sync preview / drift detection (#1531) ──────────────────────────

/** Dry-run diff of what a sync WOULD change — computed read-only. */
export interface CycleConfigSyncPreview {
  config_differs: boolean;
  /** Top-level config sections whose team value differs from the cycle's
   * snapshot — the granular drift signal. */
  fields_changed: string[];
  stations_added: string[];
  stations_removed: string[];
  tiers_changed: boolean;
  /** #1531 sentinel disclosure — stations the sync would ADD that a live
   * blanket release already covers (they count as released the moment
   * their rows publish, without a per-station Release act). */
  stations_added_already_released: string[];
}

export function cycleConfigSyncPreviewKey(cycleId: string, accountId: string | null) {
  return ["cycle-config-sync-preview", cycleId, accountId ?? ""] as const;
}

/**
 * Preview the sync (#1531): which config sections drifted from the team
 * template, the station/tier summary, and the sentinel disclosure —
 * BEFORE anything is written. Lead-only, mirrors the POST.
 */
export function useCycleConfigSyncPreview(cycleId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery<CycleConfigSyncPreview, ApiError | Error>({
    queryKey: cycleConfigSyncPreviewKey(cycleId ?? "", accountId),
    enabled: Boolean(cycleId && accountId),
    // Drift must reflect team-setup edits made moments ago in another
    // tab — don't serve a minutes-old answer to a destructive confirm.
    staleTime: 0,
    retry: false,
    queryFn: () =>
      apiFetch<CycleConfigSyncPreview>(`/api/cycles/${cycleId}/config/sync-from-team/preview`),
  });
}

// ─── Promote cycle → team default ────────────────────────────────────

/**
 * Copy this cycle's whole config onto the team default template.
 * Future cycles start from the promoted config; existing cycles keep
 * their own snapshots untouched. Lead-only on the backend. Returns the
 * updated team config — used to refresh any team-config cache.
 */
export function usePromoteCycleConfig(cycleId: string | undefined, teamId: string | undefined) {
  const { accountId } = useAuth();
  const qc = useQueryClient();
  return useMutation<TeamConfig, ApiError | Error, void>({
    mutationFn: () =>
      apiFetch<TeamConfig>(`/api/cycles/${cycleId}/promote-to-team-default`, {
        method: "POST",
      }),
    onSuccess: (next) => {
      // The team default template just changed — refresh the team-config
      // cache so the team-side Setup pages reflect the promoted config.
      qc.setQueryData(["team-config", teamId, accountId], next);
      qc.invalidateQueries({ queryKey: ["team-config", teamId, accountId] });
    },
  });
}
